import * as React from "react";

import type { ComponentNode } from "@shared/types";

import { useBuilderStore } from "@/lib/builder/builder-store";
import { getMaterial } from "@/lib/builder/materials";
import { initGeometry } from "@/lib/builder/wasm-geometry";

import {
  DomRenderer,
  SvgRenderer,
  type CanvasRenderMode,
  type CanvasRenderer,
  type CanvasRendererContext,
} from "./canvas-renderer";

// 兼容旧导入（pages/builder.tsx 依赖此常量）
export { CANVAS_DROP_ZONE_ID } from "./canvas-renderer";

/** 渲染模式切换控件（DOM 默认，SVG 为 SVG+WASM 混合渲染）。5.4 token：浅色/深色变体成对消费。 */
function CanvasModeToggle({
  mode,
  onChange,
}: {
  mode: CanvasRenderMode;
  onChange: (mode: CanvasRenderMode) => void;
}) {
  return (
    <div
      data-testid="canvas-mode-toggle"
      className="mb-3 inline-flex items-center rounded-md border border-[var(--border-tech)] bg-[var(--bg-secondary-light)] p-0.5 text-xs text-[var(--text-secondary)] dark:bg-[var(--bg-secondary)]"
    >
      {(
        [
          ["dom", "DOM"],
          ["svg", "SVG"],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          data-testid={`canvas-mode-${value}`}
          aria-pressed={mode === value}
          onClick={() => onChange(value)}
          className={cnModeButton(mode === value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function cnModeButton(active: boolean): string {
  return [
    "rounded px-2.5 py-1 transition-colors",
    active
      ? "bg-[var(--accent-primary-light)] text-white dark:bg-[var(--accent-primary)]"
      : "hover:bg-muted hover:text-[var(--text-primary-light)] dark:hover:text-[var(--text-primary)]",
  ].join(" ");
}

/** 在组件树中查找节点（画布 aria-live 播报选中组件名）。 */
function findNode(node: ComponentNode, id: string): ComponentNode | undefined {
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const hit = findNode(child, id);
    if (hit) return hit;
  }
  return undefined;
}

const DOM_RENDERER = new DomRenderer();
const SVG_RENDERER = new SvgRenderer();

export function BuilderCanvas() {
  const { schema, activePageId, selectedNodeId, selectNode, moveChild } = useBuilderStore();
  const [mode, setMode] = React.useState<CanvasRenderMode>("dom");
  const [hoveredNodeId, setHoveredNodeId] = React.useState<string | null>(null);
  const page = schema.pages.find((p) => p.id === activePageId);

  // 预加载 wasm 几何内核；加载失败自动降级纯 TS，不阻塞渲染
  React.useEffect(() => {
    void initGeometry();
  }, []);

  if (!page) {
    return <div className="p-8 text-center text-muted-foreground">请先创建一个页面</div>;
  }

  const renderer: CanvasRenderer = mode === "dom" ? DOM_RENDERER : SVG_RENDERER;

  const ctx: CanvasRendererContext = {
    node: page.layout,
    selectedNodeId,
    hoveredNodeId,
    onSelect: selectNode,
    onHover: setHoveredNodeId,
    onMoveChild: moveChild,
  };

  // 画布状态播报（aria-live）：选中/取消选中组件时通知辅助技术
  const selectedNode = selectedNodeId ? findNode(page.layout, selectedNodeId) : undefined;
  const selectionAnnouncement = selectedNode
    ? `已选中组件：${getMaterial(selectedNode.type)?.label ?? selectedNode.type}`
    : "未选中组件";

  return (
    <div
      data-testid="builder-canvas"
      className="h-full overflow-auto rounded-lg border bg-[var(--bg-primary-light)] p-4 dark:bg-[var(--bg-primary)] dark:bg-[radial-gradient(circle,#ffffff08_1px,transparent_1px)] bg-[radial-gradient(circle,#00000008_1px,transparent_1px)] [background-size:16px_16px]"
    >
      <CanvasModeToggle mode={mode} onChange={setMode} />
      <div aria-live="polite" role="status" data-testid="canvas-status" className="sr-only">
        {selectionAnnouncement}
      </div>
      {renderer.render(ctx)}
    </div>
  );
}
